import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';

import { LeadStatsLeadContextAdapter } from 'common/data/lead/stats/lead-context-adapter';
import { PropertyModel } from 'common/data/property/model';
import { PropertyContactedModel } from 'common/data/property-contacted/model';
import { PropertyContactedTypeEnum } from 'common/data/property-contacted/type.enum';
import { dateToIso } from 'common/helper/date/to-iso';
import { LeadEmailForm } from 'common/module/lead/email/form';
import { LeadEmailFormEvent } from 'common/module/lead/email/form.event';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { LeadEmailFormTypeEnum } from 'common/module/lead/email/form/type.enum';
import { PropertyCtaInteractionEvent } from 'common/module/property-cta/interaction.event';
import { PropertyCtaStatechartActionEnum } from 'common/module/property-cta/statechart/action.enum';
import { PropertyCtaStatechartPayloadInterface } from 'common/module/property-cta/statechart/payload.interface';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { StatsTealiumConversionEvent } from 'common/module/stats/tealium/conversion-event';
import { StatsTealiumDataLayerEventActionEnum } from 'common/module/stats/tealium/data-layer/event-action.enum';
import { StatsTealiumDataLayerEventLabelEnum } from 'common/module/stats/tealium/data-layer/event-label.enum';
import { StatsTealiumDataLayerTealiumEventEnum } from 'common/module/stats/tealium/data-layer/tealium-event.enum';
import { StatsTealiumDataLayerUserInfoInterface } from 'common/module/stats/tealium/data-layer/user-info.interface';
import { StatsTealiumEmailSignupEvent } from 'common/module/stats/tealium/email-signup.event';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';
import { PropertyContactedStoreServiceInterface } from 'common/service/property-contacted-store/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';

export class PropertyLeadEmailStore {
  /**
   * Statechart payload
   */
  private payload: PropertyCtaStatechartPayloadInterface = null;

  /**
   * Constructor
   */
  constructor(
    private leadEmailForm: LeadEmailForm,
    private statsService: StatsServiceInterface,
    private propertyContactedStoreService: PropertyContactedStoreServiceInterface,
    private propertyCtaStatechartStore: StatechartStoreInterface,
    private statsTealium: StatsTealiumProviderInterface
  ) {}

  /**
   * Init store
   */
  public initialize(): void {
    this.leadEmailForm.initialize();

    this.leadEmailForm.getEventEmitter().addListener(LeadEmailFormEvent.submit, this.onSubmitEmail);

    this.propertyCtaStatechartStore
      .getEventEmitter()
      .addListener(PropertyCtaStatechartActionEnum.openEmailLeadPopup, this.onOpenEmailLeadPopupActionStatechart);
  }

  /**
   * Get lead email form
   */
  public getLeadEmailForm(): LeadEmailForm {
    return this.leadEmailForm;
  }

  /**
   * Save property to contacted properties list
   */
  private saveToContacted(propertyId: string, type: PropertyContactedTypeEnum): void {
    this.propertyContactedStoreService.save(propertyId, type);

    const contactedModel = new PropertyContactedModel();

    contactedModel.property_id = parseInt(propertyId, 10);
    contactedModel.contact_date = dateToIso(new Date());
    contactedModel.contact_type = type;

    this.propertyContactedStoreService.saveInDb(contactedModel);
  }

  /**
   * Submitted lead email form
   */
  private onSubmitEmail = (input: LeadEmailFormInputInterface): void => {
    const context: StatsContextLocalInterface = {
      ...this.payload.statsContextLocal,
      lead: new LeadStatsLeadContextAdapter(
        input,
        this.payload.interactionEvent === PropertyCtaInteractionEvent.bookAViewingButtonClick
      ).getData(),
    };

    const model = <PropertyModel>this.leadEmailForm.getModel();

    // Log stats
    this.statsService.propertyLeadSend(parseInt(model.id, 10), context);

    this.statsTealium.send(StatsTealiumEmailSignupEvent(input.email.value, input?.createAlert?.value));

    const userInfo: StatsTealiumDataLayerUserInfoInterface = {
      user_full_name: input.name?.value,
      user_email: input.email?.value,
      user_phone_number: input.phone?.value,
    };

    this.statsTealium.send(
      StatsTealiumConversionEvent(StatsTealiumDataLayerTealiumEventEnum.emailNow, model, {
        eventAction: StatsTealiumDataLayerEventActionEnum.email,
        eventLabel: StatsTealiumDataLayerEventLabelEnum.email,
        userInfo,
      })
    );

    // Save property to contacted properties
    this.saveToContacted(model.id, PropertyContactedTypeEnum.email);
  };

  /**
   * Open lead email form
   */
  private onOpenEmailLeadPopupActionStatechart = (payload: PropertyCtaStatechartPayloadInterface): void => {
    this.payload = payload;
    this.leadEmailForm.getFormStrategy().initialize({
      model: payload.propertyModel,
      type: payload.type,
    });
    this.leadEmailForm.initState();
    this.leadEmailForm.updateModel(payload.propertyModel);

    if (payload.type !== LeadEmailFormTypeEnum.liveViewingPopup) {
      this.leadEmailForm.render();
    }
  };
}
